import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import apiClient from "../common/http.service";

export const useCourses = (
    page = 1,
    limit = 10,
    search = "",
    durationUnit = "",
    level = "",
    pricingTier = "",
    sort = "",
    minPrice = "",
    maxPrice = ""
) => {
    const queryClient = useQueryClient();

    const queryKey = [
        "courses", page, limit, search, durationUnit, level, pricingTier, sort, minPrice, maxPrice,
    ];

    const courseQuery = useQuery({
        queryKey,
        queryFn: async () => {
            // 🌟 FIX: Send 'limit' instead of 'pageSize' to match the backend expectation
            const params = {
                page,
                limit, 
            };

            if (search) params.search = search;
            if (durationUnit) params.durationUnit = durationUnit;
            if (level) params.level = level;
            if (pricingTier) params.pricingTier = pricingTier;
            if (sort) params.sort = sort;
            if (minPrice) params.minPrice = minPrice;
            if (maxPrice) params.maxPrice = maxPrice;

            const queryString = new URLSearchParams(params).toString();

            const response = await apiClient.get(`/course?${queryString}`);
            const payload = response?.data?.data || {};

            return {
                courses: Array.isArray(payload.data) ? payload.data : [],
                pagination: payload.pagination || {
                    totalRecords: 0,
                    totalPages: 1,
                },
            };
        },
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        keepPreviousData: true,
    });

    const createMutation = useMutation({
        mutationFn: (courseData) => apiClient.post("/course", courseData),
        onSuccess: () => {
            toast.success("Course created successfully");
            queryClient.invalidateQueries({ queryKey: ["courses"] });
        },
        onError: (err) => toast.error(err.response?.data?.message || "Create failed"),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => apiClient.put(`/course/${id}`, data),
        onSuccess: () => {
            toast.success("Course updated successfully");
            queryClient.invalidateQueries({ queryKey: ["courses"] });
        },
        onError: (err) => toast.error(err.response?.data?.message || "Update failed"),
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => apiClient.delete(`/course/${id}`),
        onSuccess: () => {
            toast.success("Course deleted successfully");
            queryClient.invalidateQueries({ queryKey: ["courses"] });
        },
        onError: (err) => toast.error(err.response?.data?.message || "Delete failed"),
    });

    return {
        courses: courseQuery.data?.courses || [],
        pagination: courseQuery.data?.pagination || {},
        isLoading: courseQuery.isLoading,
        isError: courseQuery.isError,
        error: courseQuery.error,
        createCourse: createMutation.mutateAsync,
        isCreating: createMutation.isPending,
        updateCourse: updateMutation.mutateAsync,
        isUpdating: updateMutation.isPending,
        deleteCourse: deleteMutation.mutateAsync,
        isDeleting: deleteMutation.isPending,
    };
};